import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BRAND, whatsappLink } from "../mock/mock";
import { Mail, MapPin, Phone, Instagram, Send, Clock, Home } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import { WhatsAppIcon } from "../components/WhatsAppFloat";

const ContactPage = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General enquiry", message: "" });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.message) {
      toast({ title: "Please fill required fields", description: "Name and message are required." });
      return;
    }
    const text = [
      `Hello Agrawal Rich Dry Fruits,`,
      ``,
      `I have an enquiry.`,
      ``,
      `Name: ${form.name}`,
      form.email && `Email: ${form.email}`,
      form.phone && `Phone: ${form.phone}`,
      `Subject: ${form.subject}`,
      ``,
      `Message:`,
      form.message,
    ].filter(Boolean).join("\n");
    window.open(whatsappLink(text), "_blank", "noopener,noreferrer");
    toast({ title: "Opening WhatsApp", description: "Your enquiry has been prepared — just hit send." });
    setForm({ name: "", email: "", phone: "", subject: "General enquiry", message: "" });
  };

  const waHref = whatsappLink("Hello Agrawal Rich Dry Fruits, I have a query.");

  return (
    <main className="bg-cream">
      <section className="bg-[#f3ecda] border-b border-[#efe4c9]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-10 md:py-14">
          <div className="flex items-center gap-2 text-[12.5px] text-[#6b4f39] mb-2">
            <Link to="/" className="inline-flex items-center gap-1 hover:text-brand"><Home className="h-3.5 w-3.5" /> Home</Link>
            <span>/</span><span className="text-brand font-semibold">Contact</span>
          </div>
          <h1 className="font-serif-brand text-[36px] md:text-[52px] font-bold text-[#2a1a15] leading-tight">Get in Touch</h1>
          <p className="mt-2 text-[15px] text-[#6b4f39] max-w-2xl">Whether it's an order enquiry, corporate gifting, or wholesale — we'd love to hear from you.</p>
          <a href={waHref} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5b] text-white font-semibold px-5 py-2.5 rounded-full transition-colors"><WhatsAppIcon className="h-5 w-5" /> Chat on WhatsApp</a>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-12 grid md:grid-cols-[1.1fr_1fr] gap-8">
        <div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { i: Phone, l: "Call Us", v: BRAND.phone, h: `tel:${BRAND.phoneRaw}` },
              { i: Mail, l: "Email Us", v: BRAND.email, h: `mailto:${BRAND.email}` },
              { i: Instagram, l: "Follow Us", v: BRAND.instagram, h: BRAND.instagramUrl },
              { i: Clock, l: "Hours", v: "Mon–Sat, 9am to 8pm", h: null },
            ].map((c, i) => (
              <a key={i} href={c.h || undefined} target={c.h && c.h.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="bg-white border border-[#efe4c9] rounded-2xl p-5 flex items-start gap-3 hover:border-brand transition-colors">
                <div className="h-11 w-11 rounded-full bg-[#f9f1de] flex items-center justify-center shrink-0"><c.i className="h-5 w-5 text-brand" /></div>
                <div className="min-w-0">
                  <div className="text-[12px] tracking-[0.15em] font-bold text-olive">{c.l.toUpperCase()}</div>
                  <div className="text-[14.5px] text-[#2a1a15] mt-1 break-words">{c.v}</div>
                </div>
              </a>
            ))}
          </div>

          <div className="mt-5 bg-white border border-[#efe4c9] rounded-2xl p-5 flex items-start gap-3">
            <div className="h-11 w-11 rounded-full bg-[#f9f1de] flex items-center justify-center shrink-0"><MapPin className="h-5 w-5 text-brand" /></div>
            <div>
              <div className="text-[12px] tracking-[0.15em] font-bold text-olive">VISIT US</div>
              <div className="text-[14.5px] text-[#2a1a15] mt-1 leading-relaxed">{BRAND.address}</div>
            </div>
          </div>

          <div className="mt-5 rounded-2xl overflow-hidden border border-[#efe4c9]">
            <iframe
              title="Agrawal Rich Dry Fruits Location"
              src={`https://maps.google.com/maps?q=${encodeURIComponent(BRAND.mapQuery)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
              className="w-full h-72"
              loading="lazy"
            />
          </div>
        </div>

        <form onSubmit={submit} className="bg-white border border-[#efe4c9] rounded-2xl p-6 md:p-8 h-fit">
          <div className="text-[11px] tracking-[0.28em] font-bold text-olive">SEND A MESSAGE</div>
          <h2 className="font-serif-brand text-[26px] font-bold text-[#2a1a15] mt-1">We'd love to hear from you</h2>
          <p className="mt-1 text-[12.5px] text-[#6b4f39]">Your enquiry will be sent to us on WhatsApp — quick and personal.</p>

          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            <div><label className="text-[12.5px] font-medium text-[#3a2a20]">Full Name *</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-11 bg-cream border-[#e3d3ad]" placeholder="Your name" /></div>
            <div><label className="text-[12.5px] font-medium text-[#3a2a20]">Phone</label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 h-11 bg-cream border-[#e3d3ad]" placeholder="+91 …" /></div>
          </div>
          <div className="mt-3"><label className="text-[12.5px] font-medium text-[#3a2a20]">Email</label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" className="mt-1 h-11 bg-cream border-[#e3d3ad]" placeholder="you@email.com" /></div>
          <div className="mt-3"><label className="text-[12.5px] font-medium text-[#3a2a20]">Subject</label>
            <select value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1 h-11 w-full rounded-md bg-cream border border-[#e3d3ad] px-3 text-[14px] text-[#2a1a15]">
              <option>General enquiry</option><option>Order support</option><option>Corporate gifting</option><option>Bulk / Wholesale</option><option>Partnership</option>
            </select>
          </div>
          <div className="mt-3"><label className="text-[12.5px] font-medium text-[#3a2a20]">Message *</label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={5} className="mt-1 bg-cream border-[#e3d3ad]" placeholder="Tell us how we can help..." /></div>

          <Button type="submit" className="mt-5 w-full h-12 bg-[#25D366] hover:bg-[#1ebe5b] text-white font-semibold">
            <WhatsAppIcon className="h-5 w-5 mr-1.5" /> Send via WhatsApp
          </Button>
          <p className="mt-3 text-[12px] text-[#6b4f39] text-center">Or call us at <b>{BRAND.phone}</b></p>
        </form>
      </section>
    </main>
  );
};

export default ContactPage;
